<template>
  <sfs-form-modal
    :id="modalId"
    :title="modalTitle"
    @on-hide="resetForm"
  >
    <b-form
      novalidate
      @submit.stop.prevent="handleSubmit"
    >
      <b-row>
        <b-col>
          <b-alert
            :show="baseServerErrors.length > 0"
            variant="danger"
            dismissible
            @dismissed="baseServerErrors = []"
          >
            <p
              class="mb-0"
              v-for="(error, index) in baseServerErrors"
              :key="index"
            >
              {{ error }}
            </p>
          </b-alert>
        </b-col>
      </b-row>

      <b-row
        v-if="isBulkSelection"
        class="mb-3"
      >
        <b-col cols="12">
          <b-alert
            show
            variant="info"
            class="mb-0"
          >
            Work order cancellations will be processed in the background. You can continue working while they complete.
          </b-alert>
        </b-col>
      </b-row>

      <b-row
        v-if="isBulkSelection && nonCancellableWorkOrders.length"
        class="mb-2"
      >
        <b-col>
          <b-alert
            show
            variant="warning"
          >
            The selected Work Orders can't be cancelled and will be skipped:
            {{ nonCancellableWorkOrders.map((wo) => wo.number || `#${wo.id}`).join(', ') }}
          </b-alert>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <b-form-group label="Choose a reason for cancelling">
            <v-select
              id="advanced-filter-statuses"
              v-model="selectedOption"
              :options="cancellationOptions"
              :reduce="(reason) => reason.value"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <hr class="mt-4">

      <b-row>
        <b-col
          offset-lg="6"
          lg="3"
          order="2"
          order-lg="1"
        >
          <b-button
            variant="link-dark"
            block
            id="work-order-cancel-close-button"
            @click="closeModal"
          >
            Cancel
          </b-button>
        </b-col>
        <b-col
          lg="3"
          order="1"
          order-lg="2"
        >
          <b-button
            id="work-order-cancel-submit-button"
            block
            type="submit"
            variant="primary"
            :disabled="processing"
          >
            Save
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </sfs-form-modal>
</template>

<script>
import errorToastMixin from '@/mixins/errorToastMixin';

export default {
  name: 'WorkOrdersCancelModal',
  mixins: [errorToastMixin],
  props: {
    workOrder: {
      type: Object,
      default: null,
    },
    workOrders: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      cancellationOptions: [],
      selectedOption: null,
      modalId: 'work-order-cancel-modal',
      baseServerErrors: [],
      processing: false,
    };
  },
  computed: {
    isBulkSelection() {
      return Array.isArray(this.workOrders) && this.workOrders.length > 0;
    },
    modalTitle() {
      if (this.isBulkSelection) {
        return `Cancel ${this.cancellableWorkOrders.length} Work Orders`;
      }
      return `Cancel Work Order ${this.workOrder?.number || ''}`;
    },
    cancellableWorkOrders() {
      if (!this.isBulkSelection) {
        return [];
      }
      return this.workOrders.filter((workOrder) => this.$can('cancel', workOrder));
    },
    nonCancellableWorkOrders() {
      if (!this.isBulkSelection) {
        return [];
      }
      return this.workOrders.filter((workOrder) => !this.$can('cancel', workOrder));
    },
  },
  mounted() {
    this.getCancellationOptions();
  },
  methods: {
    async getCancellationOptions() {
      const response = await this.$workOrdersAPI.getCancellationOptions();
      const options = response.data.cancellation_options.map((option) => {
        return { label: option[0], value: option[1] };
      });
      this.cancellationOptions = [...options];
    },
    async handleSubmit() {
      this.baseServerErrors = [];

      if (this.isBulkSelection) {
        if (!this.cancellableWorkOrders.length) {
          return;
        }

        this.processing = true;
        try {
          const response = await this.$workOrdersAPI.bulkCancelWorkOrders({}, this.buildBulkCancelPayload());
          this.showBackgroundProcessingToast();
          this.$emit('cancelled', response);
          this.closeModal();
        } catch (error) {
          await this.processServerErrors(error);
        } finally {
          this.processing = false;
        }
        return;
      }

      this.processing = true;
      try {
        const updateResponse = await this.$workOrdersAPI.destroy(this.workOrder.id, {
          data: { cancellation_reason: this.selectedOption },
        });
        this.$emit('cancellation-reason-changed', updateResponse);
        this.closeModal();
      } catch (error) {
        await this.processServerErrors(error);
      } finally {
        this.processing = false;
      }
    },
    buildBulkCancelPayload() {
      const payload = {
        work_order_ids: this.cancellableWorkOrders.map((workOrder) => Number(workOrder.id)),
      };
      if (this.selectedOption) {
        payload.cancellation_reason = this.selectedOption;
      }
      return payload;
    },
    async processServerErrors(error) {
      const message = await this.extractErrorMessage(error, 'Unable to cancel work orders.');
      this.baseServerErrors = [message];
    },
    showBackgroundProcessingToast() {
      if (!this.$bvToast) {
        return;
      }

      this.$bvToast.toast('Work order cancellations are being processed in the background.', {
        title: 'Cancellations queued',
        toaster: 'b-toaster-bottom-right',
        variant: 'info',
        autoHideDelay: 5000,
        appendToast: false,
      });
    },
    resetForm() {
      this.selectedOption = null;
      this.baseServerErrors = [];
      this.processing = false;
    },
    closeModal() {
      this.resetForm();
      this.$bvModal.hide(this.modalId);
    },
  },
};
</script>
